import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '..';
import {
    ISoilProfileExtObjectsResModel,
    ISoilProfileFieldTypeResModel,
    ISoilProfileLayerSampleModel,
    ISoilProfileMethodsResModel,
    ISoilProfilePlaceGeoNames,
    ISoilProfilesExtSourceLinkResModel,
    IUserManagementSearchModel
} from '../../util/Request/requestType';

interface ISelectedSoilAnalysis {
    id: number
    layer: string
    layerID: number
    profileCode: string
    profileName: string
    sampleCode: string
    sampleDate: string
    status: string
    profileID: number
    userID: string
    publisherID: string
}

interface ISoilProfileData {
    ecoSystemMethods?: ISoilProfilesExtSourceLinkResModel[]
    estimationMethods?: ISoilProfileMethodsResModel[]
    objectTypes?: ISoilProfileExtObjectsResModel[]
    fieldTypes?: ISoilProfileFieldTypeResModel[]
    publisher?: IUserManagementSearchModel
    selectEcoSystem?: string
    selectEstimationMethod?: number
    selectObjectType?: ISoilProfileExtObjectsResModel
    selectFieldType?: number
    selectPublisher?: IUserManagementSearchModel
    selectFieldTypeStr?: string
    name?: string
    coordinate?: string
    data?: IData[]
    objectID?: string
    desc?: string
    contributor?: string
    isStandard?: boolean
    placeName?: ISoilProfilePlaceGeoNames
    status?: string
    countryName?: string
    actionType?: 'add' | 'edit'
    profileid?: number,
    // use in show detail of the analysis
    selectedSoilAnalysis?: ISelectedSoilAnalysis
    selectedSoilAnalysisVisible?: boolean
    fieldRequiredCheckModel?: boolean
    clearTag?: number
}

interface IData {
    key: string
    layer: string
    rank: number
    method: IMethod
    soilAnalysis?: ISoilProfileLayerSampleModel[]
    profileCode?: string
    profileName?: string
    status?: string
    layerID?: number,
    startDepth: number,
    endDepth: number
}

interface IMethod {
    id: number
    methodID: number
    methodName: string
    name: string
    propertyMeasurementID: number
    propertyName: string
    unitName: string
}

const initialState: ISoilProfileData = {
    actionType: 'add',
    ecoSystemMethods: [],
    estimationMethods: [],
    objectTypes: [],
    fieldTypes: [],
    name: undefined,
    coordinate: undefined,
    data: [{
        key: 'soil-profile-0',
        layer: undefined,
        rank: 1,
        method: undefined,
        startDepth: 0,
        endDepth: 0
    }],
    isStandard: false,
    selectPublisher: {
        key: '',
        userId: '',
        userLoginName: '',
        userDisplayName: '',
        userEmail: '',
        userIsActive: '',
        userLastLogin: '',
        roleId: 0,
        roleName: ''
    },
    selectObjectType: {
        externalReferenceId: 0,
        externalReferenceSystem: '',
        externalReferenceObject: '',
        externalReferenceDescription: ''
    },
    selectedSoilAnalysisVisible: false,
    fieldRequiredCheckModel: false,
    clearTag: 0
};

export const soilprofilesCreatSlice = createSlice({
    name: 'soilprofilesList',
    initialState,
    reducers: {
        updateEcoSystems: (state, action: PayloadAction<ISoilProfilesExtSourceLinkResModel[]>) => {
            return {
                ...state,
                ecoSystemMethods: action.payload
            };
        },
        updateEstimationMethods: (state, action: PayloadAction<ISoilProfileMethodsResModel[]>) => {
            return {
                ...state,
                estimationMethods: action.payload
            };
        },
        updateFieldTypes: (state, action: PayloadAction<ISoilProfileFieldTypeResModel[]>) => {
            return {
                ...state,
                fieldTypes: action.payload
            };
        },
        updateObjectType: (state, action: PayloadAction<ISoilProfileExtObjectsResModel[]>) => {
            return {
                ...state,
                objectTypes: action.payload
            };
        },
        updateName: (state, action: PayloadAction<string>) => {
            return {
                ...state,
                name: action.payload,
            };
        },
        updateCoordinate: (state, action: PayloadAction<string>) => {
            return {
                ...state,
                coordinate: action.payload,
            };
        },
        updateData: (state, action: PayloadAction<IData[]>) => {
            const result = {
                ...state,
                data: action.payload
            };
            return result;
        },
        updateSelectEstimationSystem: (state, action: PayloadAction<string>) => {
            return {
                ...state,
                selectEcoSystem: action.payload
            };
        },
        updateSelectObjectType: (state, action: PayloadAction<ISoilProfileExtObjectsResModel>) => {
            return {
                ...state,
                selectObjectType: action.payload
            };
        },
        updateObjectID: (state, action: PayloadAction<string>) => {
            return {
                ...state,
                objectID: action.payload
            };
        },
        updateSelectFieldType: (state, action: PayloadAction<number>) => {
            return {
                ...state,
                selectFieldType: action.payload
            };
        },
        updateSelectPublisher: (state, action: PayloadAction<IUserManagementSearchModel>) => {
            return {
                ...state,
                publisher: action.payload,
                selectPublisher: action.payload
            };
        },
        updatePlaceName: (state, action: PayloadAction<ISoilProfilePlaceGeoNames>) => {
            return {
                ...state,
                placeName: action.payload,
            };
        },
        resetTheSoilCreatProfiles: (state) => {
            return {
                ...initialState,
                fieldTypes: state.fieldTypes,
                ecoSystemMethods: state.ecoSystemMethods,
                estimationMethods: state.estimationMethods,
                selectFieldType: state.fieldTypes[0].fieldTypeId,
                clearTag: state.clearTag + 1
            };
        },
        editOrShowTheSoilProfileModel: (state, action: PayloadAction<ISoilProfileData>) => {
            const model = {
                ...state,
                ...action.payload
            };
            return model;
        },
        updateLayerDetailPartOfProfileModel: (state, action: PayloadAction<IData[]>) => {
            const model = {
                ...state,
                data: action.payload
            };
            return model;
        },
        updateDesc: (state, action: PayloadAction<string>) => {
            const model ={
                ...state,
                desc: action.payload
            };
            return model;
        },
        updateSelectedSoilAnalysis: (state, action: PayloadAction<ISelectedSoilAnalysis>) => {
            const model ={
                ...state,
                selectedSoilAnalysis: action.payload
            };
            return model;
        },
        updateSelectedSoilAnalysisVisible: (state, action: PayloadAction<boolean>) => {
            const model ={
                ...state,
                selectedSoilAnalysisVisible: action.payload
            };
            return model;
        },
        updateTheFieldRequiredCheckModel: (state, action: PayloadAction<boolean>) => {
            const model ={
                ...state,
                fieldRequiredCheckModel: action.payload
            };
            return model;
        },
    }
});

export type {
    ISoilProfileData,
    IData
};

export const {
    updateEstimationMethods,
    updateEcoSystems,
    updateFieldTypes,
    updateObjectType,
    updateName,
    updateCoordinate,
    updateData,
    updateSelectEstimationSystem,
    updateSelectObjectType,
    updateSelectFieldType,
    updateSelectPublisher,
    updateObjectID,
    updatePlaceName,
    resetTheSoilCreatProfiles,
    editOrShowTheSoilProfileModel,
    updateDesc,
    updateSelectedSoilAnalysis,
    updateSelectedSoilAnalysisVisible,
    updateTheFieldRequiredCheckModel,
    updateLayerDetailPartOfProfileModel
} = soilprofilesCreatSlice.actions;

export const getAllHaveDefienedProfile = (state:RootState) => state.soilPageData;
export default soilprofilesCreatSlice.reducer;